import dot from 'dot-prop-immutable';
import { denormalize } from 'normalizr';
import reduceReducers from 'reduce-reducers';

import { createEntitiesReducer } from 'modules/common/redux';

import { types } from './actions';
import { TAB_TYPES } from './constants';
import * as entities from './entities';

const initialState = {
  activePartUUIDs: [],
  activeShareIds: [],
  activeTab: TAB_TYPES.OUR_PARTS,
  archivedParts: [],
  archivedPartsCount: 0,
  areFilesUploading: false,
  draftedQuotes: [],
  entities: {},
  hasMoreQuotes: false,
  inArchiveMode: false,
  inRestoreMode: false,
  isActionBarOpen: false,
  isAddToQuoteDrawerOpen: false,
  isCopyingPartsToQuote: false,
  isCreatingQuote: false,
  isDrawerOpen: false,
  isFetchingAdditionalQuotes: false,
  isFetchingArchivedParts: false,
  isFetchingDraftedQuotes: false,
  isFetchingParts: false,
  isFetchingShares: false,
  isHeaderOpen: true,
  isMergePartsDrawerOpen: false,
  isMergingParts: false,
  isNestedAddToQuoteDrawerOpen: false,
  page: 1,
  partCount: 0,
  partFilesUploading: [],
  partStatuses: {},
  quotesPage: 1,
  searchString: '',
  selectedParts: {},
  selectedQuote: undefined,
  shareCount: 0,
};

const entitiesReducer = createEntitiesReducer(
  ['parts', 'shares'],
  [
    types.PARTS_LIST_SUCCESS,
    types.RECEIVE_PART_ENTITIY,
    types.SHARES_LIST_SUCCESS,
    types.UPLOAD_PART_FILE_SUCCESS,
  ],
);

const reducer = (state, action) => {
  const { payload, type } = action;
  switch (type) {
    case types.PART_LIBRARY_CHANGE_TAB:
      return {
        ...state,
        isActionBarOpen: false,
        isHeaderOpen: true,
        selectedParts: {},
      };
    case types.PART_LIBRARY_PAGE_CLOSE_DRAWER:
      return {
        ...state,
        isDrawerOpen: false,
      };
    case types.PART_LIBRARY_PAGE_INIT:
      return {
        ...state,
        activePartUUIDs: [],
        activeShareIds: [],
        page: 1,
        pendingParts: [],
      };
    case types.PART_LIBRARY_PAGE_OPEN_DRAWER:
      return {
        ...state,
        isDrawerOpen: true,
      };
    case types.PART_LIBRARY_PAGE_SET_PAGE:
      return {
        ...state,
        page: payload.page,
      };
    case types.PART_LIBRARY_PAGE_SET_SEARCH:
      return {
        ...state,
        searchString: payload.searchString,
      };
    case types.PART_LIBRARY_PAGE_SET_TAB:
      return {
        ...state,
        activeTab: payload.tab,
      };
    case types.PART_LIBRARY_PAGE_SET_SELECTED_PARTS:
      return {
        ...state,
        selectedParts: payload.selectedParts,
      };
    case types.PARTS_LIST_ERROR:
      return {
        ...state,
        isFetchingParts: false,
      };
    case types.PARTS_LIST_REQUEST:
      return {
        ...state,
        isFetchingParts: true,
      };
    case types.PARTS_LIST_SUCCESS:
      return {
        ...state,
        activePartUUIDs: payload.result,
        isFetchingParts: false,
        partCount: payload.count,
      };
    case types.RECEIVE_PART_STATUSES:
      return dot.merge(state, 'partStatuses', action.payload.partStatuses);
    case types.SHARES_LIST_ERROR:
      return {
        ...state,
        isFetchingShares: false,
      };
    case types.SHARES_LIST_REQUEST:
      return {
        ...state,
        isFetchingShares: true,
      };
    case types.SHARES_LIST_SUCCESS:
      return {
        ...state,
        activeShareIds: payload.result,
        isFetchingShares: false,
        shareCount: payload.count,
      };
    case types.UPLOAD_PART_FILE_SUCCESS:
      return {
        ...state,
        activePartUUIDs: [payload.result, ...state.activePartUUIDs],
        partFilesUploading: state.partFilesUploading.filter(
          (file) => file.tempId !== payload.tempId,
        ),
        partCount: state.partCount + 1,
      };
    case types.UPLOAD_PART_FILES_ERROR:
    case types.UPLOAD_PART_FILES_SUCCESS:
      return {
        ...state,
        areFilesUploading: false,
      };
    case types.UPLOAD_PART_FILES_REQUEST:
      return {
        ...state,
        areFilesUploading: true,
      };
    case types.UPLOAD_PART_FILES_SET_FILES:
      return {
        ...state,
        partFilesUploading: [
          ...state.partFilesUploading,
          ...payload.partFilesUploading,
        ],
      };
    case types.PART_LIBRARY_PAGE_OPEN_ACTION_BAR:
      return {
        ...state,
        isActionBarOpen: true,
        isHeaderOpen: false,
      };
    case types.PART_LIBRARY_PAGE_CLOSE_ACTION_BAR:
      return {
        ...state,
        isActionBarOpen: false,
        isHeaderOpen: true,
        isAddToQuoteDrawerOpen: false,
        isMergePartsDrawerOpen: false,
        isNestedAddToQuoteDrawerOpen: false,
        isCreatingQuote: false,
        selectedParts: {},
        selectedQuote: undefined,
      };
    case types.PART_LIBRARY_PAGE_ARCHIVE_PARTS_SUCCESS:
      return {
        ...state,
        isActionBarOpen: false,
        isHeaderOpen: false,
        isDrawerOpen: false,
        selectedParts: {},
      };
    case types.PART_LIBRARY_PAGE_RESTORE_PARTS_SUCCESS:
      return {
        ...state,
        isActionBarOpen: false,
        isHeaderOpen: false,
        isDrawerOpen: false,
        selectedParts: {},
      };
    case types.PART_LIBRARY_PAGE_PURGE_PARTS_SUCCESS:
      return {
        ...state,
        isActionBarOpen: false,
        isHeaderOpen: false,
        isDrawerOpen: false,
        selectedParts: {},
      };
    case types.COPY_PARTS_TO_QUOTE_ERROR:
      return {
        ...state,
        isCopyingPartsToQuote: false,
      };
    case types.COPY_PARTS_TO_QUOTE_REQUEST:
      return {
        ...state,
        isCopyingPartsToQuote: true,
      };
    case types.COPY_PARTS_TO_QUOTE_SUCCESS:
      return {
        ...state,
        isActionBarOpen: false,
        isAddToQuoteDrawerOpen: false,
        isCopyingPartsToQuote: false,
        isDrawerOpen: false,
        isHeaderOpen: true,
        isNestedAddToQuoteDrawerOpen: false,
        selectedParts: {},
      };
    case types.ARCHIVED_PARTS_LIST_REQUEST:
      return {
        ...state,
        isFetchingArchivedParts: true,
      };
    case types.ARCHIVED_PARTS_LIST_SUCCESS:
      return {
        ...state,
        archivedParts: payload.parts,
        archivedPartsCount: payload.count,
        isFetchingArchivedParts: false,
      };
    case types.PART_LIBRARY_PAGE_OPEN_ADD_TO_QUOTE_DRAWER:
      return {
        ...state,
        isAddToQuoteDrawerOpen: true,
        quotesPage: 1,
      };
    case types.PART_LIBRARY_PAGE_CLOSE_ADD_TO_QUOTE_DRAWER:
      return {
        ...state,
        isAddToQuoteDrawerOpen: false,
      };
    case types.DRAFTED_QUOTES_REQUEST:
      return {
        ...state,
        isFetchingDraftedQuotes: true,
      };
    case types.DRAFTED_QUOTES_SUCCESS:
      return {
        ...state,
        isFetchingDraftedQuotes: false,
        draftedQuotes: payload.draftedQuotes,
        hasMoreQuotes: payload.hasMoreQuotes,
      };
    case types.PART_LIBRARY_PAGE_SELECT_QUOTE:
      return {
        ...state,
        isCreatingQuote: false,
        selectedQuote: payload.quote,
      };
    case types.PART_LIBRARY_PAGE_SET_IS_CREATING_QUOTE:
      return {
        ...state,
        isCreatingQuote: payload.isCreatingQuote,
        selectedQuote: null,
      };
    case types.PART_LIBRARY_PAGE_DESELECT_QUOTE:
      return {
        ...state,
        selectedQuote: null,
      };
    case types.PART_LIBRARY_PAGE_OPEN_NESTED_ADD_TO_QUOTE_DRAWER:
      return {
        ...state,
        isNestedAddToQuoteDrawerOpen: true,
      };
    case types.PART_LIBRARY_PAGE_CLOSE_NESTED_ADD_TO_QUOTE_DRAWER:
      return {
        ...state,
        isNestedAddToQuoteDrawerOpen: false,
      };
    case types.GET_ADDITIONAL_QUOTES_REQUEST:
      return {
        ...state,
        isFetchingAdditionalQuotes: true,
      };
    case types.GET_ADDITIONAL_QUOTES_SUCCESS: {
      const draftedQuotes = state.draftedQuotes;
      draftedQuotes.push(...payload.quotes);
      return {
        ...state,
        isFetchingAdditionalQuotes: false,
        draftedQuotes: draftedQuotes,
        quotesPage: payload.page,
        hasMoreQuotes: payload.hasMoreQuotes,
      };
    }
    case types.GET_ADDITIONAL_QUOTES_ERROR:
      return {
        ...state,
        isFetchingAdditionalQuotes: false,
      };
    case types.PART_LIBRARY_PAGE_OPEN_MERGE_PARTS_DRAWER:
      return {
        ...state,
        isMergePartsDrawerOpen: true,
      };
    case types.PART_LIBRARY_PAGE_CLOSE_MERGE_PARTS_DRAWER:
      return {
        ...state,
        isMergePartsDrawerOpen: false,
      };
    case types.MERGE_PARTS_REQUEST:
      return {
        ...state,
        isMergingParts: true,
      };
    case types.MERGE_PARTS_SUCCESS:
    case types.MERGE_PARTS_ERROR:
      return {
        ...state,
        isMergingParts: false,
      };
    default:
      return state;
  }
};

export default reduceReducers(entitiesReducer, reducer, initialState);

export const getScope = (state) => state.modules.partLibrary;

export const getActivePartUUIDs = (state) => getScope(state).activePartUUIDs;

export const getActivePartsCount = (state) => getActivePartUUIDs(state).length;

export const getActiveTab = (state) => getScope(state).activeTab;

export const getActiveShares = (state) =>
  denormalize(
    getScope(state).activeShareIds,
    [entities.shareSchema],
    getScope(state).entities,
  );

export const getArchivedParts = (state) => getScope(state).archivedParts;

export const getArchivedPartsCount = (state) =>
  getScope(state).archivedPartsCount;

export const getAreFilesUploading = (state) =>
  getScope(state).areFilesUploading;

export const getPage = (state) => getScope(state).page;

export const getTotalPartCount = (state) => getScope(state).partCount;

export const getTotalShareCount = (state) => getScope(state).shareCount;

export const getPartLibraryParts = (state) =>
  denormalize(
    getScope(state).activePartUUIDs,
    [entities.partSchema],
    getScope(state).entities,
  );

export const getPartStatuses = (state) => getScope(state).partStatuses;

export const getPartStatus = (state, partUuid) =>
  (getPartStatuses(state) || {})[partUuid];

export const getSearchString = (state) => getScope(state).searchString;

export const isFetchingArchivedParts = (state) =>
  getScope(state).isFetchingArchivedParts;

export const getIsFetchingParts = (state) => getScope(state).isFetchingParts;

export const isFetchingShares = (state) => getScope(state).isFetchingShares;

export const getSelectedPartsMap = (state) => getScope(state).selectedParts;

export const getSelectedPartsArray = (state) =>
  Object.values(getScope(state).selectedParts);

export const getSelectedPartsUuidArray = (state) =>
  Object.keys(getScope(state).selectedParts);

export const getSelectedPartsCount = (state) =>
  Object.keys(getScope(state).selectedParts).length;

export const isActionBarOpen = (state) => getScope(state).isActionBarOpen;

export const isSelectedPartsDrawerOpen = (state) =>
  getScope(state).isDrawerOpen;

export const isHeaderOpen = (state) => getScope(state).isHeaderOpen;

export const isInArchiveMode = (state) => getScope(state).inArchiveMode;

export const isAddToQuoteDrawerOpen = (state) =>
  getScope(state).isAddToQuoteDrawerOpen;

export const isNestedAddToQuoteDrawerOpen = (state) =>
  getScope(state).isNestedAddToQuoteDrawerOpen;

export const isFetchingDraftedQuotes = (state) =>
  getScope(state).isFetchingDraftedQuotes;

export const isCopyingPartsToQuote = (state) =>
  getScope(state).isCopyingPartsToQuote;

export const isCreatingQuote = (state) => getScope(state).isCreatingQuote;

export const getDraftedQuotes = (state) => getScope(state).draftedQuotes;

export const getSelectedQuote = (state) => getScope(state).selectedQuote;

export const isInRestoreMode = (state) => getScope(state).inRestoreMode;

export const hasMoreQuotes = (state) => getScope(state).hasMoreQuotes;

export const isFetchingAdditionalQuotes = (state) =>
  getScope(state).isFetchingAdditionalQuotes;

export const getQuotesPage = (state) => getScope(state).quotesPage;

export const getIsMergePartsDrawerOpen = (state) =>
  getScope(state).isMergePartsDrawerOpen;

export const getIsMergingParts = (state) => getScope(state).isMergingParts;

export const getPartFilesUploading = (state) =>
  getScope(state).partFilesUploading;
